import styled, { css } from "styled-components";
import { theme } from "../../../styles/theme";

interface ButtonProps {
  variant?: "primary" | "secondary" | "outlined" | "text" | "ghost";
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
}

// Estilos base para os botões
const baseButtonStyles = css<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  font-family: ${theme.fonts.main};
  font-weight: ${theme.fontWeights.semibold};
  border-radius: ${theme.borderRadius.lg};
  transition: all ${theme.transitions.default};
  cursor: pointer;
  letter-spacing: 0.02em;
  text-transform: none;
  position: relative;
  overflow: hidden;
  border: none;
  white-space: nowrap;

  ${({ fullWidth }) =>
    fullWidth &&
    `
    width: 100%;
  `}

  /* Efeito de ondulação ao clicar */
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }

  &:active::after {
    width: 300px;
    height: 300px;
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    pointer-events: none;
  }

  /* Melhora o foco para acessibilidade */
  &:focus-visible {
    outline: 2px solid ${theme.colors.primaryLight};
    outline-offset: 3px;
  }
`;

// Variantes de tamanho
const sizeStyles = {
  small: css`
    padding: ${theme.spacing.sm} ${theme.spacing.lg};
    font-size: ${theme.fontSizes.sm};
    min-height: 36px;
  `,
  medium: css`
    padding: ${theme.spacing.md} ${theme.spacing.xl};
    font-size: ${theme.fontSizes.base};
    min-height: 44px;
  `,
  large: css`
    padding: ${theme.spacing.lg} ${theme.spacing["2xl"]};
    font-size: ${theme.fontSizes.lg};
    min-height: 52px;
  `,
};

// Variantes de botão
const variantStyles = {
  primary: css`
    background: ${theme.colors.buttonGradient};
    color: white;
    box-shadow: ${theme.shadows.base}, 0 4px 20px rgba(30, 144, 255, 0.3);

    &:hover {
      background: ${theme.colors.buttonHover};
      box-shadow: ${theme.shadows.lg}, 0 8px 25px rgba(30, 144, 255, 0.4);
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0) scale(0.98);
      box-shadow: ${theme.shadows.sm}, 0 2px 10px rgba(30, 144, 255, 0.3);
    }
  `,
  secondary: css`
    background: rgba(30, 144, 255, 0.15);
    color: ${theme.colors.primaryLight};
    border: 1px solid ${theme.colors.primary};
    backdrop-filter: blur(8px);

    &:hover {
      background: rgba(30, 144, 255, 0.25);
      border-color: ${theme.colors.primaryLight};
      box-shadow: 0 0 20px rgba(30, 144, 255, 0.3);
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0) scale(0.98);
    }
  `,
  outlined: css`
    background: transparent;
    color: ${theme.colors.primary};
    border: 2px solid ${theme.colors.primary};

    &:hover {
      background: rgba(30, 144, 255, 0.1);
      border-color: ${theme.colors.primaryLight};
      box-shadow: 0 0 20px rgba(30, 144, 255, 0.2);
      color: ${theme.colors.primaryLight};
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0) scale(0.98);
    }
  `,
  ghost: css`
    background: transparent;
    color: ${theme.colors.text};
    border: 1px solid transparent;

    &:hover {
      background: rgba(30, 144, 255, 0.1);
      border-color: ${theme.colors.cardBorder};
      color: ${theme.colors.primaryLight};
    }

    &:active {
      transform: scale(0.98);
    }
  `,
  text: css`
    background: transparent;
    color: ${theme.colors.primary};
    border: none;
    padding-left: ${theme.spacing.sm};
    padding-right: ${theme.spacing.sm};
    min-height: auto;

    &:hover {
      color: ${theme.colors.primaryLight};
      background: rgba(30, 144, 255, 0.08);
    }

    &:active {
      transform: scale(0.98);
    }

    &::after {
      display: none;
    }
  `,
};

export const StyledButton = styled.button<ButtonProps>`
  ${baseButtonStyles}
  ${({ size = "medium" }) => sizeStyles[size]}
  ${({ variant = "primary" }) => variantStyles[variant]}
`;

// Componente para links estilizados como botões
export const ButtonLink = styled.a<ButtonProps>`
  ${baseButtonStyles}
  ${({ size = "medium" }) => sizeStyles[size]}
  ${({ variant = "primary" }) => variantStyles[variant]}
  text-decoration: none;
`;
